import React, { Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { ConfigProvider, Spin } from 'antd';
import { Provider } from 'react-redux';
import store from './store';
import { ROUTES } from './router/routes';
import AdminLayout from './layouts/AdminLayout';
import AuthLayout from './layouts/AuthLayout';
import Login from './pages/Login';
import LazyErrorBoundary from './components/LazyErrorBoundary';
import { getAccessToken, isTokenExpired, clearAuth, getUserRole } from './utils/auth';
import {
  ROLE_SUPER_ADMIN,
  ROLE_FINANCE,
  ROLE_OPERATOR,
  ROLE_VIDEO_UPLOADER,
  ROLE_CONTENT_AUDITOR,
} from './constants';

const Dashboard = lazy(() => import('./pages/Dashboard'));
const HospitalAudit = lazy(() => import('./pages/AuditCenter/HospitalAudit'));
const OperatorAudit = lazy(() => import('./pages/AuditCenter/OperatorAudit'));
const GuideAudit = lazy(() => import('./pages/AuditCenter/GuideAudit'));
const AgentAudit = lazy(() => import('./pages/AuditCenter/AgentAudit'));
const ArticleAudit = lazy(() => import('./pages/AuditCenter/ArticleAudit'));
const UserManagement = lazy(() => import('./pages/Customer/UserManagement'));
const HospitalManagement = lazy(() => import('./pages/Customer/HospitalManagement'));
const OperatorManagement = lazy(() => import('./pages/Customer/OperatorManagement'));
const GuideManagement = lazy(() => import('./pages/Customer/GuideManagement'));
const RelationManagement = lazy(() => import('./pages/Customer/RelationManagement'));
const HospitalAdmin = lazy(() => import('./pages/Customer/HospitalAdmin'));
const CustomerSourceStats = lazy(() => import('./pages/Customer/CustomerSourceStats'));
const ServicePrice = lazy(() => import('./pages/Service/ServicePrice'));
const MedicalCase = lazy(() => import('./pages/Service/MedicalCase'));
const Complaint = lazy(() => import('./pages/Service/Complaint'));
const ComplaintDetail = lazy(() => import('./pages/Service/ComplaintDetail'));
const LinkManagement = lazy(() => import('./pages/Service/LinkManagement'));
const LinkDetail = lazy(() => import('./pages/Service/LinkDetail'));
const ChatBoard = lazy(() => import('./pages/Monitor/ChatBoard'));
const RiskRules = lazy(() => import('./pages/Monitor/RiskRules'));
const RiskAlerts = lazy(() => import('./pages/Monitor/RiskAlerts'));
const PlatformKnowledge = lazy(() => import('./pages/Knowledge/PlatformKnowledge'));
const PublicHospital = lazy(() => import('./pages/Knowledge/PublicHospital'));
const RiskKnowledge = lazy(() => import('./pages/Knowledge/RiskKnowledge'));
const KnowledgeCenter = lazy(() => import('./pages/Knowledge/KnowledgeCenter'));
const RateConfig = lazy(() => import('./pages/Settlement/RateConfig'));
const FinancialReport = lazy(() => import('./pages/Settlement/FinancialReport'));
const OrderManagement = lazy(() => import('./pages/Settlement/OrderManagement'));
const RechargeManagement = lazy(() => import('./pages/Settlement/RechargeManagement'));
const WithdrawalManagement = lazy(() => import('./pages/Settlement/WithdrawalManagement'));
// 供应商提现审核（文档 9.2，life-service 体系，区别于运营主体提现）
const SupplierWithdrawals = lazy(() => import('./pages/Settlement/SupplierWithdrawals'));
const AccountBalance = lazy(() => import('./pages/Settlement/AccountBalance'));
const ConnectionCharge = lazy(() => import('./pages/Settlement/ConnectionCharge'));
const RevenueStats = lazy(() => import('./pages/Settlement/RevenueStats'));
const BalanceWarnings = lazy(() => import('./pages/Settlement/BalanceWarnings'));
const RoleManagement = lazy(() => import('./pages/System/RoleManagement'));
const OperationLog = lazy(() => import('./pages/System/OperationLog'));
const MemberManagement = lazy(() => import('./pages/System/MemberManagement'));
// 平台配置（文档 9.4：接通价格/收款账户/客服联系/开票信息）
const PlatformConfig = lazy(() => import('./pages/System/PlatformConfig'));
const QrcodeManagement = lazy(() => import('./pages/Channel/QrcodeManagement'));
const QrcodeUsers = lazy(() => import('./pages/Channel/QrcodeUsers'));
const ChannelUsers = lazy(() => import('./pages/Channel/ChannelUsers'));
// 加盟管理域
const JoinApplicationAudit = lazy(() => import('./pages/Join/ApplicationAudit'));
const JoinContractManagement = lazy(() => import('./pages/Join/ContractManagement'));
const JoinAccountManagement = lazy(() => import('./pages/Join/AccountManagement'));
const JoinTemplateConfig = lazy(() => import('./pages/Join/TemplateConfig'));
const JoinSupplierManagement = lazy(() => import('./pages/Join/SupplierManagement'));
const JoinReports = lazy(() => import('./pages/Join/Reports'));
// 社区运营域
const LifeSupplierCertAudit = lazy(() => import('./pages/Life/SupplierCertAudit'));
const LifeUserCertAudit = lazy(() => import('./pages/Life/UserCertAudit'));
const LifeActivityAudit = lazy(() => import('./pages/Life/ActivityAudit'));
const LifeContentAudit = lazy(() => import('./pages/Life/ContentAudit'));
const LifeOperationConfig = lazy(() => import('./pages/Life/OperationConfig'));
const LifeSignupSupervision = lazy(() => import('./pages/Life/SignupSupervision'));
const LifeOrderSupervision = lazy(() => import('./pages/Life/OrderSupervision'));
const LifePointsConfig = lazy(() => import('./pages/Life/PointsConfig'));
const LifeEscrow = lazy(() => import('./pages/Life/Escrow'));
const LifeDashboard = lazy(() => import('./pages/Life/Dashboard'));
// 社区内容管理（文档：管理后台社区内容管理改进需求文档 v1.3）
const LifePostPublish = lazy(() => import('./pages/Life/PostPublish'));
const LifeVideoUpload = lazy(() => import('./pages/Life/VideoUpload'));
const LifePostManagement = lazy(() => import('./pages/Life/PostManagement'));
const LifeNoticeManagement = lazy(() => import('./pages/Life/NoticeManagement'));
const LifeCommentManagement = lazy(() => import('./pages/Life/CommentManagement'));
const NotFound = lazy(() => import('./pages/Error/NotFound'));
const Forbidden = lazy(() => import('./pages/Error/Forbidden'));

const PageLoader = () => (
  <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
    <Spin size="large" tip="加载中..." />
  </div>
);

const PrivateRoute = ({ children, allowedRoles }) => {
  const token = getAccessToken();

  if (!token) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (isTokenExpired()) {
    clearAuth();
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (allowedRoles && allowedRoles.length > 0) {
    const role = getUserRole();
    if (!allowedRoles.includes(role)) {
      return <Navigate to={ROUTES.FORBIDDEN} replace />;
    }
  }

  return children;
};

const App = () => {
  return (
    <Provider store={store}>
      <ConfigProvider>
        <BrowserRouter>
          <LazyErrorBoundary>
            <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route element={<AuthLayout />}>
                <Route path={ROUTES.LOGIN} element={<Login />} />
              </Route>

              <Route
                element={
                  <PrivateRoute>
                    <AdminLayout />
                  </PrivateRoute>
                }
              >
                <Route path={ROUTES.DASHBOARD} element={<Dashboard />} />
                <Route path={ROUTES.HOSPITAL_AUDIT} element={<HospitalAudit />} />
                <Route path={ROUTES.OPERATOR_AUDIT} element={<OperatorAudit />} />
                <Route path={ROUTES.GUIDE_AUDIT} element={<GuideAudit />} />
                <Route path={ROUTES.AGENT_AUDIT} element={<AgentAudit />} />
                <Route path={ROUTES.ARTICLE_AUDIT} element={<ArticleAudit />} />
                <Route path={ROUTES.USER_MANAGEMENT} element={<UserManagement />} />
                <Route path={ROUTES.HOSPITAL_MANAGEMENT} element={<HospitalManagement />} />
                <Route path={ROUTES.OPERATOR_MANAGEMENT} element={<OperatorManagement />} />
                <Route path={ROUTES.GUIDE_MANAGEMENT} element={<GuideManagement />} />
                <Route path={ROUTES.RELATION_MANAGEMENT} element={<RelationManagement />} />
                <Route path={ROUTES.HOSPITAL_ADMIN} element={<HospitalAdmin />} />
                <Route path={ROUTES.CUSTOMER_SOURCE_STATS} element={<CustomerSourceStats />} />
                <Route path={ROUTES.SERVICE_PRICE} element={<ServicePrice />} />
                <Route path={ROUTES.MEDICAL_CASE} element={<MedicalCase />} />
                <Route path={ROUTES.COMPLAINT} element={<Complaint />} />
                <Route path={ROUTES.COMPLAINT_DETAIL} element={<ComplaintDetail />} />
                <Route path={ROUTES.LINK_MANAGEMENT} element={<LinkManagement />} />
                <Route path={ROUTES.LINK_DETAIL} element={<LinkDetail />} />
                <Route path={ROUTES.CHAT_BOARD} element={<ChatBoard />} />
                <Route path={ROUTES.RISK_RULES} element={<RiskRules />} />
                <Route path={ROUTES.RISK_ALERTS} element={<RiskAlerts />} />
                <Route path={ROUTES.PLATFORM_KNOWLEDGE} element={<PlatformKnowledge />} />
                <Route path={ROUTES.PUBLIC_HOSPITAL} element={<PublicHospital />} />
                <Route path={ROUTES.RISK_KNOWLEDGE} element={<RiskKnowledge />} />
                <Route path={ROUTES.KNOWLEDGE_CENTER} element={<KnowledgeCenter />} />
                <Route path={ROUTES.RATE_CONFIG} element={<RateConfig />} />
                <Route path={ROUTES.FINANCIAL_REPORT} element={<FinancialReport />} />
                <Route path={ROUTES.ORDER_MANAGEMENT} element={<OrderManagement />} />
                <Route path={ROUTES.RECHARGE_MANAGEMENT} element={<RechargeManagement />} />
                <Route path={ROUTES.WITHDRAWAL_MANAGEMENT} element={<WithdrawalManagement />} />
                {/* 供应商提现审核：仅超管/财务（文档 9.2） */}
                <Route path={ROUTES.SUPPLIER_WITHDRAWALS} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN, ROLE_FINANCE]}>
                    <SupplierWithdrawals />
                  </PrivateRoute>
                } />
                <Route path={ROUTES.ACCOUNT_BALANCE} element={<AccountBalance />} />
                <Route path={ROUTES.CONNECTION_CHARGE} element={<ConnectionCharge />} />
                <Route path={ROUTES.REVENUE_STATS} element={<RevenueStats />} />
                <Route path={ROUTES.BALANCE_WARNINGS} element={<BalanceWarnings />} />
                <Route path={ROUTES.CHANNEL_QRCODES} element={<QrcodeManagement />} />
                <Route path={ROUTES.CHANNEL_QRCODE_USERS} element={<QrcodeUsers />} />
                <Route path={ROUTES.CHANNEL_USERS} element={<ChannelUsers />} />
                <Route path={ROUTES.ROLE_MANAGEMENT} element={<RoleManagement />} />
                {/* 成员管理（文档 16.1）：仅超级管理员，直接访问跳 403 */}
                <Route path={ROUTES.MEMBER_MANAGEMENT} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN]}>
                    <MemberManagement />
                  </PrivateRoute>
                } />
                <Route path={ROUTES.OPERATION_LOG} element={<OperationLog />} />
                {/* 平台配置（文档 9.4）：超管/财务 */}
                <Route path={ROUTES.PLATFORM_CONFIG} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN, ROLE_FINANCE]}>
                    <PlatformConfig />
                  </PrivateRoute>
                } />
                {/* 加盟管理域 */}
                <Route path={ROUTES.JOIN_APPLICATIONS} element={<JoinApplicationAudit />} />
                <Route path={ROUTES.JOIN_CONTRACTS} element={<JoinContractManagement />} />
                <Route path={ROUTES.JOIN_ACCOUNTS} element={<JoinAccountManagement />} />
                <Route path={ROUTES.JOIN_TEMPLATES} element={<JoinTemplateConfig />} />
                <Route path={ROUTES.JOIN_SUPPLIERS} element={<JoinSupplierManagement />} />
                <Route path={ROUTES.JOIN_REPORTS} element={<JoinReports />} />
                {/* 社区运营域 */}
                <Route path={ROUTES.LIFE_SUPPLIER_CERTS} element={<LifeSupplierCertAudit />} />
                <Route path={ROUTES.LIFE_USER_CERTS} element={<LifeUserCertAudit />} />
                <Route path={ROUTES.LIFE_ACTIVITIES} element={<LifeActivityAudit />} />
                <Route path={ROUTES.LIFE_CONTENT_AUDIT} element={<LifeContentAudit />} />
                <Route path={ROUTES.LIFE_OPERATION} element={<LifeOperationConfig />} />
                <Route path={ROUTES.LIFE_SIGNUPS} element={<LifeSignupSupervision />} />
                <Route path={ROUTES.LIFE_ORDERS} element={<LifeOrderSupervision />} />
                <Route path={ROUTES.LIFE_POINTS_CONFIG} element={<LifePointsConfig />} />
                <Route path={ROUTES.LIFE_ESCROW} element={<LifeEscrow />} />
                <Route path={ROUTES.LIFE_DASHBOARD} element={<LifeDashboard />} />
                {/* 社区内容管理（文档 v1.3）：无对应角色直接访问时跳 403 */}
                <Route path={ROUTES.LIFE_PUBLISH} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN, ROLE_OPERATOR]}>
                    <LifePostPublish />
                  </PrivateRoute>
                } />
                <Route path={ROUTES.LIFE_VIDEO_UPLOAD} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN, ROLE_VIDEO_UPLOADER]}>
                    <LifeVideoUpload />
                  </PrivateRoute>
                } />
                <Route path={ROUTES.LIFE_POSTS} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN, ROLE_CONTENT_AUDITOR]}>
                    <LifePostManagement />
                  </PrivateRoute>
                } />
                <Route path={ROUTES.LIFE_NOTICES} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN, ROLE_CONTENT_AUDITOR]}>
                    <LifeNoticeManagement />
                  </PrivateRoute>
                } />
                <Route path={ROUTES.LIFE_COMMENTS} element={
                  <PrivateRoute allowedRoles={[ROLE_SUPER_ADMIN, ROLE_CONTENT_AUDITOR]}>
                    <LifeCommentManagement />
                  </PrivateRoute>
                } />
                <Route path={ROUTES.FORBIDDEN} element={<Forbidden />} />
                <Route path="/" element={<Navigate to={ROUTES.DASHBOARD} replace />} />
                <Route path="*" element={<NotFound />} />
              </Route>
            </Routes>
            </Suspense>
          </LazyErrorBoundary>
        </BrowserRouter>
      </ConfigProvider>
    </Provider>
  );
};

export default App;
